'use client';

/**
 * Location autocomplete via api.vhire.com (server-side Google Places proxy).
 *
 * The browser never talks to Google directly — all calls flow through
 * vhire-location-service via the gateway. This is what makes it safe across
 * an unbounded set of tenant career portals (career.bootminds.com,
 * career.acme.com, …): we don't have to allowlist every tenant subdomain
 * on the Google API key, and the key itself never ships in HTML.
 *
 * Wire format matches Google Places API (New) — `sessionToken` is a UUID
 * generated client-side, reused across one autocomplete + details cycle, then
 * reset, so Google bills the pair as a single session.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { MapPin, Loader2, X, Navigation } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_BASE } from '@/lib/config';
import { getAdminToken, getCandidateToken } from '@/lib/auth-tokens';

export interface PlaceResult {
  address: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  lat: number;
  lng: number;
  placeId?: string;
  formattedAddress: string;
}

interface PlacesAutocompleteProps {
  id?: string;
  purpose: 'candidate' | 'job';
  value: string;
  onChange: (value: string, place?: PlaceResult) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  showNearbyHint?: boolean;
  restrictToCountry?: string; // e.g. 'in' for India
  restrictToCities?: boolean; // dropdown shows only cities/localities (marketplace city filter)
}

interface Prediction {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText?: string;
}

interface PlaceDetailsResponse {
  placeId: string;
  formattedAddress: string;
  lat: number;
  lng: number;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
}

function newSessionToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function PlacesAutocomplete({
  id,
  purpose,
  value,
  onChange,
  placeholder = 'Search for your location...',
  className,
  required = false,
  disabled = false,
  showNearbyHint = true,
  restrictToCountry = 'in',
  restrictToCities = false,
}: PlacesAutocompleteProps) {
  const requestHeaders = useCallback((json = false): Record<string, string> => {
    const token = purpose === 'candidate' ? getCandidateToken() : getAdminToken();
    return {
      ...(json ? { 'Content-Type': 'application/json' } : {}),
      'X-VHire-Location-Purpose': purpose,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, [purpose]);
  // Parse initial value — could be JSON (from profile) or plain string
  const parseInitialValue = (val: string): { query: string; place: PlaceResult | null } => {
    if (!val) return { query: '', place: null };
    try {
      const parsed = JSON.parse(val);
      if (parsed && typeof parsed === 'object' && (parsed.address || parsed.formattedAddress)) {
        return {
          query: parsed.formattedAddress || parsed.address,
          place: {
            address: parsed.address || parsed.formattedAddress,
            formattedAddress: parsed.formattedAddress || parsed.address,
            city: parsed.city,
            state: parsed.state,
            country: parsed.country,
            lat: parsed.lat || 0,
            lng: parsed.lng || 0,
            pincode: parsed.pincode,
            placeId: parsed.placeId,
          },
        };
      }
    } catch {
      // Not JSON, treat as plain string
    }
    return { query: val, place: null };
  };

  const initialParsed = parseInitialValue(value);
  const [query, setQuery] = useState(initialParsed.query);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(initialParsed.place);
  const [isLocating, setIsLocating] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sessionTokenRef = useRef<string>(newSessionToken());

  // Sync state when value prop changes (e.g. profile data loads async)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only react to external value changes
  useEffect(() => {
    if (value && !selectedPlace) {
      const parsed = parseInitialValue(value);
      if (parsed.place) {
        setQuery(parsed.query);
        setSelectedPlace(parsed.place);
      } else if (parsed.query) {
        setQuery(parsed.query);
      }
    }
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch predictions from our gateway (which proxies Google Places API New)
  const fetchPredictions = useCallback(async (input: string) => {
    if (input.length < 2) {
      setPredictions([]);
      return;
    }

    setIsLoading(true);
    setApiError(null);
    try {
      const response = await fetch(`${API_BASE}/places/autocomplete`, {
        method: 'POST',
        headers: requestHeaders(true),
        body: JSON.stringify({
          query: input,
          sessionToken: sessionTokenRef.current,
          countryCode: restrictToCountry,
          citiesOnly: restrictToCities,
        }),
      });

      if (!response.ok) {
        setApiError("Location search is temporarily unavailable — type your city and continue.");
        setPredictions([]);
        return;
      }

      const data = await response.json() as { predictions?: Prediction[]; error?: 'rate_limited' | 'unavailable' };
      // A 200 with an `error` flag = upstream Google failure (quota/billing/key)
      // swallowed into an empty list. Surface it as "unavailable, type & continue"
      // rather than the misleading "No locations found" (which implies the place
      // doesn't exist). The typed value is still accepted on submit.
      if (data.error) {
        setApiError("Location search is temporarily unavailable — type your city and continue.");
        setPredictions([]);
        return;
      }
      setPredictions(data.predictions || []);
    } catch {
      setApiError("Location search is temporarily unavailable — type your city and continue.");
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, [requestHeaders, restrictToCountry, restrictToCities]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query && !selectedPlace) {
        fetchPredictions(query);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, fetchPredictions, selectedPlace]);

  // Resolve a placeId to coordinates + address components via our gateway
  const getPlaceDetails = useCallback(async (placeId: string, description: string): Promise<PlaceResult> => {
    const sessionToken = sessionTokenRef.current;
    // Reset session token after this call (Google billing optimization — one
    // session = one autocomplete burst + one details fetch)
    sessionTokenRef.current = newSessionToken();

    const url = `${API_BASE}/places/details/${encodeURIComponent(placeId)}?sessionToken=${encodeURIComponent(sessionToken)}`;
    const response = await fetch(url, { headers: requestHeaders() });
    if (!response.ok) {
      throw new Error(`Place details failed: ${response.status}`);
    }
    const data = await response.json() as PlaceDetailsResponse | null;
    if (!data || !data.lat || !data.lng) {
      throw new Error('No location data returned');
    }

    return {
      address: data.formattedAddress || description,
      formattedAddress: data.formattedAddress || description,
      lat: data.lat,
      lng: data.lng,
      city: data.city,
      state: data.state,
      country: data.country,
      pincode: data.pincode,
      placeId: data.placeId || placeId,
    };
  }, [requestHeaders]);

  // Handle prediction selection
  const handleSelect = async (prediction: Prediction) => {
    setIsLoading(true);
    setShowDropdown(false);
    try {
      const placeResult = await getPlaceDetails(prediction.placeId, prediction.description);
      setSelectedPlace(placeResult);
      setQuery(placeResult.formattedAddress);
      onChange(placeResult.formattedAddress, placeResult);
    } catch {
      // If details fail, still set the text — better than losing the input
      setQuery(prediction.description);
      onChange(prediction.description);
    } finally {
      setIsLoading(false);
      setPredictions([]);
    }
  };

  // Use current location → reverse geocode via our gateway
  const useCurrentLocation = async () => {
    if (!navigator.geolocation) return;
    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `${API_BASE}/places/reverse?lat=${latitude}&lng=${longitude}`,
            { headers: requestHeaders() },
          );
          if (!response.ok) {
            setIsLocating(false);
            return;
          }
          const data = await response.json() as PlaceDetailsResponse | null;
          if (!data) {
            setIsLocating(false);
            return;
          }
          const placeResult: PlaceResult = {
            address: data.formattedAddress || '',
            formattedAddress: data.formattedAddress || '',
            lat: data.lat || latitude,
            lng: data.lng || longitude,
            city: data.city,
            state: data.state,
            country: data.country,
            pincode: data.pincode,
            placeId: data.placeId,
          };
          setSelectedPlace(placeResult);
          setQuery(placeResult.formattedAddress);
          onChange(placeResult.formattedAddress, placeResult);
        } catch {
          // Reverse geocode failed
        } finally {
          setIsLocating(false);
        }
      },
      () => setIsLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleClear = () => {
    setSelectedPlace(null);
    setQuery('');
    onChange('');
    setPredictions([]);
    sessionTokenRef.current = newSessionToken();
    inputRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);
    setSelectedPlace(null);
    setShowDropdown(true);
    onChange(newValue);
  };

  return (
    <div className={cn('relative', className)}>
      {/* API error message */}
      {apiError && (
        <p className="text-xs text-destructive mb-2">
          {apiError}
        </p>
      )}

      {/* Nearby hint */}
      {showNearbyHint && !selectedPlace && !apiError && (
        <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          We&apos;ll show jobs closest to your location first
        </p>
      )}

      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          id={id}
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={cn(
            'w-full rounded-lg border border-input bg-background py-2.5 pl-10 pr-20',
            'text-sm text-foreground placeholder:text-muted-foreground',
            'focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary',
            'disabled:bg-muted disabled:cursor-not-allowed',
            selectedPlace && 'border-green-500 bg-green-500/5'
          )}
        />

        {/* Right side icons */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {!isLoading && !selectedPlace && !isLocating && (
            <button
              type="button"
              onClick={useCurrentLocation}
              className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
              title="Use current location"
            >
              <Navigation className="h-4 w-4" />
            </button>
          )}
          {isLocating && (
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          )}
          {selectedPlace && !isLoading && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Selected location info */}
      {selectedPlace && (
        <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          Location set • Jobs near you will appear first
        </div>
      )}

      {/* Predictions dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-border bg-popover shadow-lg"
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.placeId || index}
              type="button"
              onClick={() => handleSelect(prediction)}
              className="flex w-full items-start gap-2 px-3 py-2.5 text-left hover:bg-accent transition-colors"
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <div className="font-medium text-foreground">
                  {prediction.mainText || prediction.description}
                </div>
                {prediction.secondaryText && (
                  <div className="text-xs text-muted-foreground truncate">
                    {prediction.secondaryText}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results */}
      {showDropdown && query.length >= 2 && predictions.length === 0 && !isLoading && !apiError && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover p-3 text-center text-sm text-muted-foreground shadow-lg">
          No locations found for &quot;{query}&quot;
        </div>
      )}
    </div>
  );
}
